import { createAgentPublicLeadNote } from "../services/publicSiteLeadsApi";
import { readCrmLeadOfferHandoff } from "./crmLeadHandoff";
import { countAdultsAndKids, get_depret_from_result } from "./LDSHelpers";

export const CRM_LEAD_OFFER_NOTED_EVENT = "crmLeadOfferNoted";

const HE_WEEKDAYS_SHORT = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

function emitCrmLeadOfferNoted(leadId) {
  try {
    window.dispatchEvent(
      new CustomEvent(CRM_LEAD_OFFER_NOTED_EVENT, {
        detail: { leadId: String(leadId || "") },
      })
    );
  } catch {
    // ignore
  }
}

function parseStayDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim().split(/\s+/)[0];
  const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatStayDateWithWeekday(raw) {
  if (!raw) return "";
  const d = parseStayDate(raw);
  if (!d) return String(raw).trim().split(/\s+/)[0];
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const wd = HE_WEEKDAYS_SHORT[d.getDay()] || "";
  return wd ? `${wd} ${dd}/${mm}/${yyyy}` : `${dd}/${mm}/${yyyy}`;
}

function buildStayDatesLabel(depRaw, retRaw) {
  const dep = formatStayDateWithWeekday(depRaw);
  const ret = formatStayDateWithWeekday(retRaw);
  if (dep && ret) return `${dep} – ${ret}`;
  return dep || ret || "";
}

function firstLeg(flightNode) {
  if (!flightNode) return null;
  const leg = flightNode?.leg ?? flightNode;
  if (Array.isArray(leg)) return leg[0] || null;
  return leg && typeof leg === "object" ? leg : null;
}

function formatClock(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const m = s.match(/(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return "";
}

/**
 * LDS vacation flights often store clocks inside depDate/arrDate:
 * "09/08/2026 15:20" — not in separate depTime/arrTime fields.
 */
function resolveLegDepArrClocks(leg) {
  if (!leg || typeof leg !== "object") {
    return { depTime: "", arrTime: "" };
  }
  const depFromField = formatClock(
    leg.depTime ||
      leg.DepTime ||
      leg.departureTime ||
      leg.DepartureTime ||
      ""
  );
  const arrFromField = formatClock(
    leg.arrTime ||
      leg.ArrTime ||
      leg.arrivalTime ||
      leg.ArrivalTime ||
      ""
  );
  const depFromDate = formatClock(
    leg.depDate || leg.DepDate || leg.departureDate || ""
  );
  const arrFromDate = formatClock(
    leg.arrDate || leg.ArrDate || leg.arrivalDate || ""
  );
  // Also accept ISO "2026-08-09T15:20:00"
  const depFromIso = formatClock(
    String(leg.depDate || leg.departureDateTime || leg.departureDate || "")
  );
  const arrFromIso = formatClock(
    String(leg.arrDate || leg.arrivalDateTime || leg.arrivalDate || "")
  );

  return {
    depTime: depFromField || depFromDate || depFromIso || "",
    arrTime: arrFromField || arrFromDate || arrFromIso || "",
  };
}

function formatFlightLegLine(leg, directionHe) {
  if (!leg) return "";
  const from =
    leg.depCity ||
    leg.from ||
    (typeof leg.depAirport === "object"
      ? leg.depAirport?.code
      : leg.depAirport) ||
    leg.origin ||
    leg.fromAirport ||
    "";
  const to =
    leg.arrCity ||
    leg.to ||
    (typeof leg.arrAirport === "object"
      ? leg.arrAirport?.code
      : leg.arrAirport) ||
    leg.destination ||
    leg.toAirport ||
    "";
  const { depTime, arrTime } = resolveLegDepArrClocks(leg);
  const flightNo =
    leg.flightNumber || leg.flightNo || leg.marketingFlightNumber || "";
  const route = [from, to].filter(Boolean).join(" → ");
  const bits = [directionHe];
  if (route) bits.push(String(route).trim());
  if (depTime) bits.push(`המראה ${depTime}`);
  if (arrTime) bits.push(`נחיתה ${arrTime}`);
  if (flightNo) bits.push(`טיסה ${flightNo}`);
  return bits.filter(Boolean).join(" · ");
}

function extractFlightTimesLabel(selectedResult) {
  const fromUser = selectedResult?.selectedFlightOptionFromUser;

  const fromLinkedFlights = () => {
    const linkedFlightOption =
      fromUser?.linkedFlight ||
      (Array.isArray(fromUser?.flight) ? fromUser : null) ||
      selectedResult?.linkedFlights ||
      null;

    let flights = [];
    if (Array.isArray(linkedFlightOption?.flight)) {
      flights = linkedFlightOption.flight;
    } else if (Array.isArray(linkedFlightOption) && linkedFlightOption[0]?.flight) {
      flights = Array.isArray(linkedFlightOption[0].flight)
        ? linkedFlightOption[0].flight
        : [];
    }
    const outLine = formatFlightLegLine(firstLeg(flights[0]), "הלוך");
    const inLine = formatFlightLegLine(firstLeg(flights[1]), "חזור");
    return [outLine, inLine].filter(Boolean).join("\n");
  };

  // Prefer raw LDS legs (depDate includes clock) over optional normalized snapshots
  const linkedLines = fromLinkedFlights();
  if (linkedLines && /המראה\s+\d{1,2}:\d{2}|נחיתה\s+\d{1,2}:\d{2}/.test(linkedLines)) {
    return linkedLines;
  }

  if (fromUser?.outboundFlight || fromUser?.inboundFlight) {
    const lines = [
      formatFlightLegLine(fromUser.outboundFlight, "הלוך"),
      formatFlightLegLine(fromUser.inboundFlight, "חזור"),
    ].filter(Boolean);
    const joined = lines.join("\n");
    if (joined && /המראה\s+\d{1,2}:\d{2}|נחיתה\s+\d{1,2}:\d{2}/.test(joined)) {
      return joined;
    }
    // Fall back to whatever we have (route + flight no), but prefer linked if present
    if (linkedLines) return linkedLines;
    return joined;
  }

  return linkedLines || "";
}

function extractRoomTypeLabel(slots) {
  if (!Array.isArray(slots) || !slots.length) return "";
  const parts = [];
  slots.forEach((slot, idx) => {
    const room = slot?.room || slot?.selectedRoom || null;
    if (!room) return;
    const name =
      room.roomName ||
      room.RoomName ||
      room.name ||
      room.roomType ||
      room.RoomType ||
      "";
    const board =
      room.roomBasis ||
      room.RoomBasis ||
      room.boardBasis ||
      room.board ||
      room.BoardBasis ||
      "";
    const label = [name, board].filter(Boolean).join(" · ");
    if (!label) return;
    parts.push(slots.length > 1 ? `חדר ${idx + 1}: ${label}` : label);
  });
  return parts.join(" | ");
}

function extractPaxSummary(slots) {
  if (!Array.isArray(slots) || !slots.length) return "";
  // Prefer full multi-room counts (countAdultsAndKids only uses first room by design)
  let adults = 0;
  let kids = 0;
  let infants = 0;
  for (const group of slots) {
    if (!group || typeof group !== "object") continue;
    if (Array.isArray(group.adults)) adults += group.adults.length;
    else if (Number.isFinite(Number(group.adults))) adults += Number(group.adults);
    if (Array.isArray(group.kids)) kids += group.kids.length;
    else if (Number.isFinite(Number(group.children ?? group.childs))) {
      kids += Number(group.children ?? group.childs);
    }
    if (Array.isArray(group.infants)) infants += group.infants.length;
    else if (Number.isFinite(Number(group.infants))) infants += Number(group.infants);
  }
  if (!adults && !kids && !infants) {
    const c = countAdultsAndKids(slots);
    adults = c.totalAdults;
    kids = c.totalKids;
    infants = c.totalInfant;
  }
  const roomCount = slots.filter((s) => s?.room || s?.adults).length || slots.length;
  const parts = [];
  if (roomCount > 1) parts.push(`${roomCount} חדרים`);
  else if (roomCount === 1) parts.push("חדר 1");
  if (adults) parts.push(`${adults} מבוגרים`);
  if (kids) parts.push(`${kids} ילדים`);
  if (infants) parts.push(`${infants} תינוקות`);
  return parts.join(", ");
}

/**
 * After an offer action (PDF / WhatsApp / digital link / marketing copy),
 * attach a note on the CRM lead. Safe no-op when no handoff is present.
 */
export async function noteCrmLeadOfferCreated(details = {}) {
  const handoff = readCrmLeadOfferHandoff();
  if (!handoff?.crmLeadId) return false;

  const channel = String(details.channel || "other").toLowerCase();
  const channelHe =
    channel === "pdf"
      ? "הצעת מחיר PDF"
      : channel === "whatsapp"
        ? "טקסט / שיתוף WhatsApp"
        : channel === "marketing"
          ? "העתקת מלל שיווקי"
          : channel === "digital_link" || channel === "copy_link"
            ? "קישור דיגיטלי ללקוח"
            : "הצעה";

  const feeRaw =
    details.handlingFeeDisplay ||
    (details.handlingFeeIls != null &&
    Number.isFinite(Number(details.handlingFeeIls)) &&
    Number(details.handlingFeeIls) > 0
      ? `₪ ${Math.ceil(Number(details.handlingFeeIls)).toLocaleString("he-IL")}`
      : null);

  const totalRaw =
    details.totalDisplay ||
    (details.totalIls != null &&
    Number.isFinite(Number(details.totalIls)) &&
    Number(details.totalIls) > 0
      ? `₪ ${Math.ceil(Number(details.totalIls)).toLocaleString("he-IL")}`
      : null);

  const lines = [
    `הצעה ללקוח מה-CRM · ${channelHe}`,
    `זמן: ${new Date().toLocaleString("he-IL")}`,
  ];
  if (handoff.full_name) lines.push(`לקוח: ${handoff.full_name}`);
  if (details.hotelName) lines.push(`מלון / שירות: ${details.hotelName}`);
  if (details.destination) lines.push(`יעד: ${details.destination}`);
  if (details.datesLabel) lines.push(`תאריכי שהייה: ${details.datesLabel}`);
  if (details.flightTimesLabel) {
    String(details.flightTimesLabel)
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .forEach((l) => {
        // Lines already start with הלוך/חזור
        lines.push(
          l.startsWith("הלוך") || l.startsWith("חזור")
            ? `שעות טיסה ${l}`
            : `שעות טיסה: ${l}`
        );
      });
  }
  if (details.roomType) lines.push(`סוג חדר: ${details.roomType}`);
  if (details.paxSummary) lines.push(`הרכב חדר: ${details.paxSummary}`);
  if (details.offerRef) lines.push(`מזהה הצעה: ${details.offerRef}`);
  lines.push(
    feeRaw
      ? `★ דמי טיפול שהוסיף הסוכן: ${feeRaw}`
      : "★ דמי טיפול שהוסיף הסוכן: לא צוינו / 0"
  );
  if (totalRaw) lines.push(`סכום סופי ללקוח: ${totalRaw}`);
  if (details.publicUrl) lines.push(`קישור: ${details.publicUrl}`);
  if (details.extra) lines.push(String(details.extra));

  try {
    await createAgentPublicLeadNote(handoff.crmLeadId, {
      note_text: lines.join("\n"),
    });
    emitCrmLeadOfferNoted(handoff.crmLeadId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Build note payload from vacation-packages /packages/profile context fields.
 */
export function buildVacationPackageCrmOfferDetails({
  channel,
  selectedResult,
  handleFeeData,
  ourPrice,
  orderId,
  publicUrl,
  roomsAndPaxInfo,
  extra,
} = {}) {
  const hotel =
    selectedResult?.hotel ||
    selectedResult?.selectedHotel ||
    selectedResult?.fullDeal?.hotel ||
    null;
  const hotelName =
    hotel?.DisplayName ||
    hotel?.HotelName ||
    hotel?.hotelName ||
    hotel?.name ||
    selectedResult?.dealName ||
    "";
  const destination =
    selectedResult?.destination ||
    selectedResult?.fullDeal?.destination ||
    hotel?.CityName ||
    hotel?.cityName ||
    "";

  let depRaw =
    selectedResult?.depDate ||
    selectedResult?.dep_date ||
    selectedResult?.searchPhrase?.checkIn ||
    selectedResult?.fullDeal?.depDate ||
    "";
  let retRaw =
    selectedResult?.retDate ||
    selectedResult?.ret_date ||
    selectedResult?.searchPhrase?.checkOut ||
    selectedResult?.fullDeal?.retDate ||
    "";

  if ((!depRaw || !retRaw) && selectedResult) {
    try {
      const dr = get_depret_from_result(selectedResult);
      // API returns depDate / retDate (not dep / ret)
      if (!depRaw && dr?.depDate) depRaw = dr.depDate;
      if (!retRaw && dr?.retDate) retRaw = dr.retDate;
    } catch {
      // ignore
    }
  }

  const datesLabel = buildStayDatesLabel(depRaw, retRaw) || undefined;
  const flightTimesLabel = extractFlightTimesLabel(selectedResult) || undefined;

  const slots = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  const roomType = extractRoomTypeLabel(slots) || undefined;
  const paxSummary = extractPaxSummary(slots) || undefined;

  const feeType = handleFeeData?.type;
  let handlingFeeIls;
  if (feeType === "+" && handleFeeData?.value != null) {
    handlingFeeIls = Number(handleFeeData.value);
  } else if (ourPrice?.handleFee != null) {
    handlingFeeIls = Number(ourPrice.handleFee);
  }

  const totalIls =
    ourPrice?.priceWithCreditCardFee ??
    ourPrice?.priceWithCommissionAndCreditCardFee ??
    ourPrice?.priceWithHandleFee ??
    null;

  return {
    channel: channel || "digital_link",
    offerRef: orderId ? String(orderId) : undefined,
    hotelName: hotelName || undefined,
    destination: destination || undefined,
    roomType,
    datesLabel,
    flightTimesLabel,
    paxSummary,
    handlingFeeIls:
      Number.isFinite(handlingFeeIls) && handlingFeeIls > 0
        ? handlingFeeIls
        : undefined,
    totalIls:
      totalIls != null && Number.isFinite(Number(totalIls)) && Number(totalIls) > 0
        ? Number(totalIls)
        : undefined,
    publicUrl: publicUrl || undefined,
    extra: extra || undefined,
  };
}

export async function noteCrmLeadVacationPackageOffer(ctx) {
  return noteCrmLeadOfferCreated(buildVacationPackageCrmOfferDetails(ctx));
}
